import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { findingsAPI } from '../../services/api';

/**
 * Project-level findings — per-kind cohort summaries (#52).
 *
 * Renders whatever summaries the backend reports for the project, one card per
 * result type, WITHOUT knowing any kind by name. Each summary is self-describing:
 * a title (display_name), optional declared controls (rendered as selects),
 * optional note, and a generic columns/rows table — or a placeholder when the
 * kind has no summary defined yet / a summary failed. A new result type, or a
 * new pipeline of an existing type, shows up here automatically.
 *
 * See services/findingsSummaryService for the contract.
 */

function GenericTable({ columns, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
        No rows.
      </Typography>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align || 'left'}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} hover>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'}>
                  {row[c.key] == null || row[c.key] === '' ? '—' : row[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function coverageLabel(coverage) {
  if (!coverage || coverage.of == null) return null;
  const { specimens, of } = coverage;
  const n = specimens.toLocaleString();
  const m = of.toLocaleString();
  if (specimens === of) return `All ${m} specimens with results`;
  return `${n} of ${m} specimens with results`;
}

function SummaryCard({ summary, onControlChange }) {
  const { display_name, result_type, summary_type, controls, note, columns, rows, message, coverage } =
    summary;
  const coverageText = coverageLabel(coverage);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1">{display_name}</Typography>
            {result_type && <Chip size="small" variant="outlined" label={result_type} />}
          </Stack>
          {coverageText && (
            <Typography variant="caption" color="text.secondary">
              {coverageText}
            </Typography>
          )}
        </Box>
        {controls && controls.length > 0 && (
          <Stack direction="row" spacing={1}>
            {controls.map((ctl) => (
              <TextField
                key={ctl.key}
                select
                size="small"
                label={ctl.label}
                value={ctl.value}
                onChange={(e) =>
                  onControlChange(summary.producer_name, summary.kind, ctl.key, e.target.value)
                }
                sx={{ minWidth: 150 }}
              >
                {ctl.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        )}
      </Stack>

      {note && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {note}
        </Alert>
      )}

      {summary_type === 'unsupported' && <Alert severity="info">{message}</Alert>}
      {summary_type === 'error' && <Alert severity="warning">{message}</Alert>}
      {(summary_type === 'prevalence' || summary_type === 'tally') && (
        <GenericTable columns={columns} rows={rows} />
      )}
    </Box>
  );
}

const ProjectFindingsSection = ({ projectId }) => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    findingsAPI
      .projectSummaries(projectId)
      .then((res) => active && setSummaries(res.data.summaries || []))
      .catch((err) => {
        if (active) setError(err.response?.data?.msg || err.message || 'Failed to load findings');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [projectId]);

  // A control change re-requests just that one pipeline's card (scoped by
  // producer + kind) and swaps it in. Optimistically set the control value so
  // the select responds immediately, then replace with the authoritative result.
  const cardId = (s) => `${s.producer_name}:${s.kind}`;

  const handleControlChange = (producer, kind, key, value) => {
    const id = `${producer}:${kind}`;
    setSummaries((prev) =>
      prev.map((s) =>
        cardId(s) === id
          ? { ...s, controls: s.controls.map((c) => (c.key === key ? { ...c, value } : c)) }
          : s
      )
    );
    findingsAPI
      .projectSummaries(projectId, { producer, kind, [key]: value })
      .then((res) => {
        const updated = (res.data.summaries || [])[0];
        if (updated) setSummaries((prev) => prev.map((s) => (cardId(s) === id ? updated : s)));
      })
      .catch(() => {
        /* keep the optimistic value; the table just won't refresh */
      });
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading project findings…</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load project findings: {error}</Alert>;
  }

  if (summaries.length === 0) {
    return (
      <Alert severity="info">
        No analysis findings for this project's specimens yet. Once a pipeline's outputs are
        ingested for specimens in this project, a summary will appear here for each result type.
      </Alert>
    );
  }

  return (
    <Stack spacing={3} divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
      {summaries.map((summary) => (
        <SummaryCard key={cardId(summary)} summary={summary} onControlChange={handleControlChange} />
      ))}
    </Stack>
  );
};

export default ProjectFindingsSection;
